import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Badge, Button, Card, CardContent, CardHeader, CardTitle, EmptyState, useToast } from '@adop/ui';
import { Bell, BellOff, CheckCircle2, RefreshCw, UtensilsCrossed } from 'lucide-react';
import { formatCents, formatLocalDateTime } from '@adop/shared';
import { useWorkspace } from '../../lib/workspace-context';
import {
  listOrderItemsByOrderIds,
  listOrders,
  markOrderPaid,
  subscribeToLocation,
  updateOrderStatus,
  type OrderItemRow,
  type OrderRow,
} from '../../lib/data';
import { groupTableBoardOrders, isOpenDineInOrder, type TableBoardOrderInput } from '../../lib/table-board';
import { playNewOrderBeep, unlockAudio } from '../../lib/beep';
import { errorMessage } from '../../lib/api';
import { relativeTime } from '../../lib/format';
import { Alert } from '../../components/Alert';
import { PageHeader } from '../../components/PageHeader';
import { OrderStatusBadge, PaymentStatusBadge } from '../../components/StatusBadge';
import { LoadingBlock } from '../../components/LoadingState';

const SOUND_KEY = 'adop.platform.tableboard.sound';
const BOARD_ACTIONS = ['accepted', 'ready', 'completed'] as const;
const ACTION_LABELS: Record<string, string> = {
  accepted: 'Accept',
  ready: 'Ready',
  completed: 'Complete',
};

export function TableBoardPage() {
  const { location } = useWorkspace();
  const { toast } = useToast();
  const [orders, setOrders] = useState<OrderRow[]>([]);
  const [items, setItems] = useState<OrderItemRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [soundEnabled, setSoundEnabled] = useState(() => {
    try {
      return window.localStorage.getItem(SOUND_KEY) !== 'off';
    } catch {
      return true;
    }
  });
  const soundRef = useRef(soundEnabled);
  soundRef.current = soundEnabled;

  const load = useCallback(async () => {
    if (!location) return;
    try {
      setError(null);
      const rows = await listOrders(location.id, 200);
      setOrders(rows);
      const openIds = rows.filter(isOpenDineInOrder).map((order) => order.id);
      setItems(await listOrderItemsByOrderIds(openIds));
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setLoading(false);
    }
  }, [location]);

  useEffect(() => {
    setLoading(true);
    void load();
    if (!location) return;
    const channel = subscribeToLocation(location.id, ['orders'], (table, eventType) => {
      void load();
      if (table === 'orders' && eventType === 'INSERT') {
        if (soundRef.current) playNewOrderBeep();
        // Line items are inserted right after the order; give them a beat.
        window.setTimeout(() => void load(), 1200);
      }
    });
    return () => {
      void channel.unsubscribe();
    };
  }, [location, load]);

  const itemCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const item of items) {
      counts.set(item.order_id, (counts.get(item.order_id) ?? 0) + item.quantity);
    }
    return counts;
  }, [items]);

  const boardOrders: TableBoardOrderInput[] = useMemo(
    () =>
      orders.map((order) => ({
        id: order.id,
        order_number: order.order_number,
        order_type: order.order_type,
        status: order.status,
        table_label: order.table_label,
        payment_status: order.payment_status,
        payment_method: order.payment_method,
        total_cents: order.total_cents,
        created_at: order.created_at,
        item_count: itemCounts.get(order.id) ?? 0,
      })),
    [orders, itemCounts],
  );

  const groups = useMemo(() => groupTableBoardOrders(boardOrders), [boardOrders]);

  const toggleSound = () => {
    const next = !soundEnabled;
    setSoundEnabled(next);
    try {
      window.localStorage.setItem(SOUND_KEY, next ? 'on' : 'off');
    } catch {
      // ignore
    }
    if (next) unlockAudio();
  };

  const setStatus = async (orderId: string, status: string) => {
    setBusyId(orderId);
    try {
      await updateOrderStatus(orderId, status);
      toast({ title: `Order marked ${status}`, variant: 'success' });
      await load();
    } catch (cause) {
      toast({ title: 'Could not update order', description: errorMessage(cause), variant: 'error' });
    } finally {
      setBusyId(null);
    }
  };

  const pay = async (orderId: string) => {
    setBusyId(orderId);
    try {
      await markOrderPaid(orderId);
      toast({ title: 'Order marked paid', variant: 'success' });
      await load();
    } catch (cause) {
      toast({ title: 'Could not mark paid', description: errorMessage(cause), variant: 'error' });
    } finally {
      setBusyId(null);
    }
  };

  if (!location) {
    return <EmptyState icon={<UtensilsCrossed className="h-6 w-6" />} title="Select a location" />;
  }

  return (
    <div className="space-y-5">
      <PageHeader
        title="Table board"
        description="Open dine-in orders, grouped by table. Accept, fire and settle from here."
        action={
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={toggleSound}>
              {soundEnabled ? <Bell className="h-4 w-4" /> : <BellOff className="h-4 w-4" />}
              {soundEnabled ? 'Sound on' : 'Sound off'}
            </Button>
            <Button variant="outline" size="sm" onClick={() => void load()}>
              <RefreshCw className="h-4 w-4" /> Refresh
            </Button>
          </div>
        }
      />

      {error ? <Alert>{error}</Alert> : null}

      {loading ? (
        <LoadingBlock />
      ) : groups.length === 0 ? (
        <EmptyState
          icon={<UtensilsCrossed className="h-6 w-6" />}
          title="No open table orders"
          description="When a guest scans the QR code at their table and orders, their order appears here grouped by table."
          action={
            <Link to="/dashboard/tables" className="text-sm font-medium text-zinc-900 underline">
              Set up table QR codes →
            </Link>
          }
        />
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          {groups.map((group) => (
            <Card key={group.table_label}>
              <CardHeader>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <CardTitle>Table {group.table_label}</CardTitle>
                  <div className="flex flex-wrap items-center gap-2 text-xs text-zinc-500">
                    <Badge variant="outline">
                      {group.item_count} {group.item_count === 1 ? 'item' : 'items'}
                    </Badge>
                    <span>{formatCents(group.total_cents)}</span>
                    <span>opened {relativeTime(group.opened_at)}</span>
                    {group.unpaid_count > 0 ? (
                      <Badge variant="warning">{group.unpaid_count} unpaid</Badge>
                    ) : null}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {group.orders.map((order) => {
                  const actions = BOARD_ACTIONS.filter((target) => {
                    if (order.status === 'placed') return true;
                    if (order.status === 'accepted') return target !== 'accepted';
                    if (order.status === 'ready') return target === 'completed';
                    return false;
                  });
                  return (
                    <div
                      key={order.id}
                      className="rounded-lg border border-zinc-200 p-3 text-sm"
                    >
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-semibold text-zinc-900">#{order.order_number}</span>
                        <OrderStatusBadge status={order.status} />
                        <PaymentStatusBadge status={order.payment_status} />
                        <span className="ml-auto text-xs text-zinc-500">
                          {relativeTime(order.created_at)}
                        </span>
                      </div>
                      <p className="mt-1 text-xs text-zinc-500">
                        Open {formatLocalDateTime(order.created_at, location.timezone)} ·{' '}
                        {order.item_count} {order.item_count === 1 ? 'item' : 'items'} ·{' '}
                        {formatCents(order.total_cents)}
                        {order.payment_method === 'card' ? ' · Card' : ' · Pay in store'}
                      </p>
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        {actions.map((target) => (
                          <Button
                            key={target}
                            size="sm"
                            variant={target === 'completed' ? 'secondary' : 'primary'}
                            loading={busyId === order.id}
                            onClick={() => void setStatus(order.id, target)}
                          >
                            {target === 'completed' ? <CheckCircle2 className="h-4 w-4" /> : null}
                            {ACTION_LABELS[target]}
                          </Button>
                        ))}
                        {order.payment_status === 'unpaid' ? (
                          <Button
                            size="sm"
                            variant="outline"
                            loading={busyId === order.id}
                            onClick={() => void pay(order.id)}
                          >
                            Mark paid
                          </Button>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
